import React, { useContext, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { Link as RouterLink, useMatch, useNavigate } from 'react-router-dom';

import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import { COLORS } from '../Constant/COLORS';

import AuthContext from '../Context/AuthContext';

import useHandleLogout from '../Hooks/useHandleLogout';
import { USER_TYPES } from '../Constant/Enums';
import { useIsMobile } from '../Hooks/useIsMobile';

const TypoNav = styled(Typography)({
  color: COLORS.blue,
  fontWeight: 500,
});

const useIsUserPage = () =>
  Boolean(
    useMatch({
      path: '/user/:userID',
      caseSensitive: false,
      end: false,
    })
  );

const useIsSellerOrCategoryPage = () => {
  const isSellerPage = Boolean(
    useMatch({
      path: '/professional',
      caseSensitive: false,
      end: false,
    })
  );
  const isCategoryPage = Boolean(
    useMatch({
      path: '/category',
      caseSensitive: false,
      end: false,
    })
  );

  return isSellerPage || isCategoryPage;
};

// The professional register / landing area (`/professional`, `/registration`,
// `/login`, `/status`) — NOT the public seller page `/professional/:serviceId`.
// On this area the header stays minimal (Home only), so Join/Sign In are hidden.
const useIsRegisterProfessionalArea = () => {
  const isRoot = Boolean(useMatch('/professional'));
  const isRegistration = Boolean(useMatch('/professional/registration'));
  const isLogin = Boolean(useMatch({ path: '/professional/login', end: false }));
  const isStatus = Boolean(useMatch('/professional/status'));
  return isRoot || isRegistration || isLogin || isStatus;
};

export const useIsLoggedUser = () => {
  const { user } = useContext(AuthContext);

  return Boolean(
    [USER_TYPES.USER, USER_TYPES.PROFESSIONAL, USER_TYPES.ADMIN].includes(user?.type)
  );
};

export const NavItemHome = () => {
  const isUserPage = useIsUserPage();

  const isProfessionalPage = Boolean(
    useMatch({
      path: '/professional/:professionalID',
      caseSensitive: false,
      end: false,
    })
  );
  const isRegisterProfessionalHome = Boolean(
    useMatch({
      path: '/professional',
      caseSensitive: false,
    })
  );

  //render only if its one of user page or professional page
  if (isUserPage || isProfessionalPage || isRegisterProfessionalHome)
    return (
      <Button
        className='nav-item'
        LinkComponent={RouterLink}
        to='/'
        size='large'
        variant='text'
        color={isUserPage ? 'blue' : undefined}
        sx={{ color: isUserPage ? COLORS.blue : 'white' }}>
        Home
      </Button>
    );
};

export const NavItemLogout = () => {
  const isUserPage = useIsUserPage();
  const handleLogout = useHandleLogout();
  const navigate = useNavigate();

  if (isUserPage)
    return (
      <Button
        className='nav-item'
        variant='outlined'
        color='blue'
        onClick={async () => {
          await handleLogout();
          navigate('/');
        }}
      >
        <TypoNav>Logout</TypoNav>
      </Button>
    );
};

export const NavItemJoin = () => {
  const isHomePage = Boolean(useMatch('/'));
  const isSellerOrCategoryPage = useIsSellerOrCategoryPage();
  const isRegisterProfessionalArea = useIsRegisterProfessionalArea();
  const isLogged = useIsLoggedUser();

  const isMobile = useIsMobile();

  if (
    (isHomePage || isSellerOrCategoryPage) &&
    !isRegisterProfessionalArea &&
    !isLogged
  )
    return (
      <Button
        className='nav-item'
        size={isMobile ? undefined : 'small'}
        LinkComponent={RouterLink}
        to='/professional'
        variant='outlined'
        color='blue'
        sx={{ borderWidth: '2px !important' }}
      >
        <TypoNav variant={isMobile ? undefined : 'body1'}>Join</TypoNav>
      </Button>
    );
};

export const NavItemLoggedUser = () => {
  const { user } = useContext(AuthContext);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const profileBtnRef = useRef();
  const navigate = useNavigate();

  const handleLogout = useHandleLogout();
  const isHomePage = Boolean(useMatch('/'));
  const isLogged = useIsLoggedUser();

  const handleNavigate = () => {
    switch (user.type) {
      case USER_TYPES.USER:
        return navigate(`/user/dashboard/my-details`);
      case USER_TYPES.PROFESSIONAL:
        return navigate(`/professional/dashboard/my-details`);
      case USER_TYPES.ADMIN:
        return navigate(`/admin/dashboard`);
      //no default
    }
  };

  if (isHomePage && isLogged)
    return (
      <>
        <IconButton
          ref={profileBtnRef}
          onClick={() => setIsAccountMenuOpen(true)}
        >
          <AccountCircleIcon fontSize='large' htmlColor={COLORS.blue} />
        </IconButton>
        <Menu
          onClose={() => setIsAccountMenuOpen(false)}
          open={isAccountMenuOpen}
          anchorEl={profileBtnRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <MenuItem role='button' onClick={handleNavigate}>
            Account
          </MenuItem>
          <MenuItem
            role='button'
            onClick={async () => {
              setIsAccountMenuOpen(false);
              await handleLogout();
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </>
    );
};

export const NavItemDashboard = () => {
  const { user } = useContext(AuthContext);
  const isHomePage = Boolean(useMatch('/'));
  const isSellerOrCategoryPage = useIsSellerOrCategoryPage();
  const isMobile = useIsMobile();

  // Only a logged-in professional gets a Dashboard shortcut on the public pages.
  if (
    (isHomePage || isSellerOrCategoryPage) &&
    user?.type === USER_TYPES.PROFESSIONAL
  )
    return (
      <Button
        className='nav-item'
        size={isMobile ? undefined : 'small'}
        LinkComponent={RouterLink}
        to='/professional/dashboard/my-details'
        variant='contained'
        color='blue'
        sx={{ borderWidth: '2px !important' }}
      >
        <Typography
          variant={isMobile ? undefined : 'body1'}
          color='white'
          fontWeight={500}
        >
          Dashboard
        </Typography>
      </Button>
    );
};

export const NavItemSignIn = ({ onClick }) => {
  const isHomePage = Boolean(useMatch('/'));
  const isLogged = useIsLoggedUser();
  const isSellerOrCategoryPage = useIsSellerOrCategoryPage();
  // Hidden on the professional register/landing area (it has its own in-page
  // Login button); still shown on home, category and public seller pages.
  const isRegisterProfessionalArea = useIsRegisterProfessionalArea();

  if (
    (isHomePage || isSellerOrCategoryPage) &&
    !isRegisterProfessionalArea &&
    !isLogged
  )
    return (
      <Button
        color='white1'
        variant='text'
        onClick={onClick}
        className='nav-item'
      >
        <TypoNav variant='body1'>Sign In</TypoNav>
      </Button>
    );
};

NavItemSignIn.propTypes = { onClick: PropTypes.func };
